// src/App.tsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { players as legacyBasePlayersArr } from "../shared/data/rankings";
import type { Position, Player } from "../shared/models/Player";

import RankingsList from "./components/RankingsList";
import Board from "./components/Board";
import type { BoardTab, DraftStyle } from "./components/boardTypes";
import TopBanner from "../shared/components/TopBanner";
import { type CompareRankingsColumn } from "../shared/components/CompareRankingsView";
import MobileDraftCompanionView from "./components/MobileDraftCompanionView";
import DraftSyncPanel from "./components/DraftSyncPanel";
import { DEFAULT_STARTER_CONFIG, type TeamOverridesState } from "../shared/components/TeamsBoard";

import { posColor } from "../shared/utils/posColor";
import { exportCheatsheetPdf } from "./utils/cheatsheetPdf";
import { usePlayers } from "../shared/hooks/usePlayers";
import { useSleeperDirectory } from "../shared/hooks/useSleeperDirectory";
import { importExpertRankingsFile } from "./utils/expertRankingsImport";

import {
  emptyTiersByPos,
  exportRankingsCsv,
  exportRankingsXlsx,
  importRankingsXlsx,
  type RankingsListKey,
  type TiersByPos,
} from "../shared/utils/xlsxRankings";

import { DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { applyDraftSyncPayload, type DraftSyncPayload } from "./utils/draftSync";
import { loadDraftSyncRequest, type DraftSyncRequest } from "./utils/draftSyncProviders";
import { loadMobileDraftSavedState, saveMobileDraftSavedState } from "./storage/mobileDraftStorage";
import {
  getDesktopUiScale,
  loadStoredBoolean,
  shouldUseTouchLayout,
} from "./features/shared/appUtils";
import { arraysEqual, getRankedRoundForPlayer } from "./features/shared/boardUtils";
import {
  cloneVisibleTiersForRankingIds,
  filterRankingIdsByVisibility,
  type ImportedCompareList,
  normalizeImportedTierBreaks,
} from "./features/shared/draftCompanionHelpers";

export default function App() {
  const savedMobileDraftState = useMemo(() => loadMobileDraftSavedState(), []);
  const hasHydratedMobileDraftRef = useRef(false);
  const saveMobileDraftTimeoutRef = useRef<number | null>(null);
  const importPickerFocusTimeoutRef = useRef<number | null>(null);
  const importPickerFallbackTimeoutRef = useRef<number | null>(null);
  const [showDraftSyncModal, setShowDraftSyncModal] = useState(false);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [showImportPickerBackdrop, setShowImportPickerBackdrop] = useState(false);
  const [lastDraftSyncRequest, setLastDraftSyncRequest] = useState<DraftSyncRequest | null>(
    savedMobileDraftState?.draft?.lastDraftSyncRequest ?? null
  );
  const [isRefreshingDraftSync, setIsRefreshingDraftSync] = useState(false);
  const [isMobile, setIsMobile] = useState(() => shouldUseTouchLayout());
  const [desktopUiScale, setDesktopUiScale] = useState(() => (shouldUseTouchLayout() ? 1 : getDesktopUiScale()));
  const [teams, setTeams] = useState(() => savedMobileDraftState?.draft?.teams ?? 12);
  const [teamNames, setTeamNames] = useState<string[]>(
    savedMobileDraftState?.draft?.teamNames?.length
      ? savedMobileDraftState.draft.teamNames
      : Array.from({ length: savedMobileDraftState?.draft?.teams ?? 12 }, (_, i) => `Team ${i + 1}`)
  );
  const [rosterStarterConfig, setRosterStarterConfig] = useState(() => ({
    ...DEFAULT_STARTER_CONFIG,
    ...(savedMobileDraftState?.draft?.rosterStarterConfig ?? {}),
  }));
  const [rosterTeamOverrides, setRosterTeamOverrides] = useState<TeamOverridesState>(
    savedMobileDraftState?.draft?.rosterTeamOverrides ?? {}
  );

  useEffect(() => {
    setTeamNames((prev) => Array.from({ length: teams }, (_, i) => prev[i] ?? `Team ${i + 1}`));
  }, [teams]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const compactMedia = window.matchMedia("(max-width: 1100px)");
    const standaloneMedia = window.matchMedia("(display-mode: standalone)");
    const pointerMedia = window.matchMedia("(pointer: coarse)");
    const update = () => {
      const nextMobile = shouldUseTouchLayout();
      setIsMobile(nextMobile);
      setDesktopUiScale(nextMobile ? 1 : getDesktopUiScale());
    };
    update();
    compactMedia.addEventListener("change", update);
    standaloneMedia.addEventListener("change", update);
    pointerMedia.addEventListener("change", update);
    window.addEventListener("orientationchange", update);
    window.addEventListener("resize", update);
    return () => {
      compactMedia.removeEventListener("change", update);
      standaloneMedia.removeEventListener("change", update);
      pointerMedia.removeEventListener("change", update);
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return undefined;

    const previousContent = viewportMeta.getAttribute("content");
    viewportMeta.setAttribute(
      "content",
      isMobile
        ? "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
        : "width=device-width, initial-scale=1, viewport-fit=cover"
    );

    return () => {
      viewportMeta.setAttribute("content", previousContent ?? "width=device-width, initial-scale=1");
    };
  }, [isMobile]);

  const [draftStyle, setDraftStyle] = useState<DraftStyle>(savedMobileDraftState?.draft?.draftStyle ?? "Snake Draft");

  // ----- fixed rankings lists -----
  const [rankingsListKey, setRankingsListKey] = useState<RankingsListKey>("Rankings");

  const [hideKickers, setHideKickers] = useState(() => loadStoredBoolean("fantasy-board:hide-kickers", false));
  const [hideDefenses, setHideDefenses] = useState(() => loadStoredBoolean("fantasy-board:hide-defenses", false));
  const [expertImport, setExpertImport] = useState<ImportedCompareList | null>(savedMobileDraftState?.expertImport ?? null);
  const [compareColumnOrder, setCompareColumnOrder] = useState<string[]>(["my-rankings", "expert-rankings"]);
  const [rankingIdsByList, setRankingIdsByList] = useState<Record<RankingsListKey, string[]>>(() => ({
    Rankings: savedMobileDraftState?.rankingIdsByList?.Rankings ?? [],
    Experts: savedMobileDraftState?.rankingIdsByList?.Experts ?? [],
  }));

  const [tiersByPosByList, setTiersByPosByList] = useState<Record<RankingsListKey, TiersByPos>>(() => ({
    Rankings: savedMobileDraftState?.tiersByPosByList?.Rankings ?? emptyTiersByPos(),
    Experts: savedMobileDraftState?.tiersByPosByList?.Experts ?? emptyTiersByPos(),
  }));

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:hide-kickers", String(hideKickers));
  }, [hideKickers]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:hide-defenses", String(hideDefenses));
  }, [hideDefenses]);

  const {
    sleeperPlayers,
    canonicalizePlayers,
    canonicalizeRankingIds,
  } = useSleeperDirectory({ legacyPlayers: legacyBasePlayersArr });

  const rankingIds = rankingIdsByList[rankingsListKey];
  const tiersByPos = tiersByPosByList[rankingsListKey];

  function moveRankings(fromIndex: number, toIndex: number) {
    setRankingIdsByList((prev) => {
      const next = arrayMove(prev.Rankings, fromIndex, toIndex);
      return { ...prev, Rankings: next };
    });
  }

  // Board should reflect the active rankings list tab.
  const boardTiersByPos = tiersByPosByList[rankingsListKey];

  const onUpdateTiersByPos = useCallback(
    (pos: Position, tierBreaks: string[]) => {
      setTiersByPosByList((prev) => ({
        ...prev,
        [rankingsListKey]: {
          ...prev[rankingsListKey],
          [pos]: tierBreaks,
        },
      }));
    },
    [rankingsListKey]
  );

  // ----- drafted state -----
  const [draftedIds, setDraftedIds] = useState<Set<string>>(
    () => new Set(savedMobileDraftState?.draft?.draftedIds ?? [])
  );
  const [draftedOrder, setDraftedOrder] = useState<string[]>(savedMobileDraftState?.draft?.draftedOrder ?? []);

  // ----- favorite state -----
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(
    () => new Set(savedMobileDraftState?.planner?.favoriteIds ?? [])
  );
  const [plannerRoundById, setPlannerRoundById] = useState<Record<string, number>>(
    savedMobileDraftState?.planner?.plannerRoundById ?? {}
  );
  const [plannerOrderedIds, setPlannerOrderedIds] = useState<string[]>(
    savedMobileDraftState?.planner?.plannerOrderedIds ?? []
  );

  function toggleFavorite(id: string) {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Rankings list position tab
  const [activeTab, setActiveTab] = useState<"Overall" | Position>("Overall");
  const [boardTab, setBoardTab] = useState<BoardTab>("Rankings Board");

  // ----- players (base + expert import + imported extras) -----
  const [expertPlayers, setExpertPlayers] = useState<Player[]>(savedMobileDraftState?.expertPlayers ?? []);
  const canonicalLegacyBasePlayers = useMemo(
    () => canonicalizePlayers(legacyBasePlayersArr),
    [canonicalizePlayers]
  );
  const playerIdentitySource = useMemo(
    () => [...legacyBasePlayersArr, ...expertPlayers],
    [expertPlayers]
  );

  const basePlayers = useMemo(() => {
    const mergedById = new Map<string, Player>();

    for (const source of [canonicalLegacyBasePlayers, sleeperPlayers, expertPlayers]) {
      for (const player of source) {
        const existing = mergedById.get(player.id);
        mergedById.set(player.id, {
          ...existing,
          ...player,
        });
      }
    }

    return Array.from(mergedById.values());
  }, [canonicalLegacyBasePlayers, expertPlayers, sleeperPlayers]);

  const { extraPlayers, setExtraPlayers, playersById } = usePlayers({
    basePlayers,
    initialExtraPlayers: savedMobileDraftState?.extraPlayers ?? [],
  });

  useEffect(() => {
    if (!playerIdentitySource.length) return;

    setRankingIdsByList((prev) => {
      const next: Record<RankingsListKey, string[]> = {
        Rankings: canonicalizeRankingIds(prev.Rankings ?? [], playerIdentitySource),
        Experts: canonicalizeRankingIds(prev.Experts ?? [], playerIdentitySource),
      };

      if (
        arraysEqual(prev.Rankings ?? [], next.Rankings) &&
        arraysEqual(prev.Experts ?? [], next.Experts)
      ) {
        return prev;
      }

      return next;
    });
  }, [canonicalizeRankingIds, playerIdentitySource]);
  const filteredExpertRankingIds = useMemo(() => {
    return filterRankingIdsByVisibility(rankingIdsByList.Experts ?? [], playersById, hideKickers, hideDefenses);
  }, [rankingIdsByList.Experts, playersById, hideKickers, hideDefenses]);
  const boardRankingIds = useMemo(() => {
    return filterRankingIdsByVisibility(rankingIdsByList[rankingsListKey], playersById, hideKickers, hideDefenses);
  }, [rankingIdsByList, rankingsListKey, playersById, hideKickers, hideDefenses]);
  const setExpertsAsRankings = useCallback(() => {
    if (!filteredExpertRankingIds.length) return;
    setRankingIdsByList((prev) => ({ ...prev, Rankings: [...filteredExpertRankingIds] }));
    setTiersByPosByList((prev) => ({
      ...prev,
      Rankings: cloneVisibleTiersForRankingIds(filteredExpertRankingIds, prev.Experts ?? emptyTiersByPos(), playersById),
    }));
  }, [filteredExpertRankingIds, playersById]);
  const setActiveSourceAsRankings = rankingsListKey === "Experts" ? setExpertsAsRankings : undefined;

  const addPlayerToRankings = useCallback(
    (name: string, position: Position) => {
      const trimmed = name.trim();
      if (!trimmed) return;

      const uuid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? (crypto as any).randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const id = `custom-${uuid}`;

      const newPlayer: Player = {
        id,
        name: trimmed,
        position,
      };

      setExtraPlayers((prev) => [newPlayer, ...prev]);

      setRankingIdsByList((prev) => ({
        Rankings: [id, ...prev.Rankings.filter((x) => x !== id)],
        Experts: [id, ...prev.Experts.filter((x) => x !== id)],
      }));
    },
    [setExtraPlayers, setRankingIdsByList]
  );

  const removeAddedPlayer = useCallback(
    (playerId: string) => {
      if (!playerId.startsWith("custom-")) return;

      setExtraPlayers((prev) => prev.filter((player) => player.id !== playerId));
      setRankingIdsByList((prev) => ({
        Rankings: prev.Rankings.filter((id) => id !== playerId),
        Experts: prev.Experts.filter((id) => id !== playerId),
      }));
      setTiersByPosByList((prev) => {
        const next = { ...prev };
        (Object.keys(next) as RankingsListKey[]).forEach((listKey) => {
          next[listKey] = {
            QB: next[listKey].QB.filter((id) => id !== playerId),
            RB: next[listKey].RB.filter((id) => id !== playerId),
            WR: next[listKey].WR.filter((id) => id !== playerId),
            TE: next[listKey].TE.filter((id) => id !== playerId),
            K: next[listKey].K.filter((id) => id !== playerId),
            DST: next[listKey].DST.filter((id) => id !== playerId),
          };
        });
        return next;
      });
      setFavoriteIds((prev) => {
        if (!prev.has(playerId)) return prev;
        const next = new Set(prev);
        next.delete(playerId);
        return next;
      });
      setPlannerRoundById((prev) => {
        if (!(playerId in prev)) return prev;
        const next = { ...prev };
        delete next[playerId];
        return next;
      });
      setPlannerOrderedIds((prev) => prev.filter((id) => id !== playerId));
      setDraftedIds((prev) => {
        if (!prev.has(playerId)) return prev;
        const next = new Set(prev);
        next.delete(playerId);
        return next;
      });
      setDraftSlots((prev) => prev.map((slotId) => (slotId === playerId ? null : slotId)));
    },
    [setExtraPlayers]
  );

  // ----- draft slots -----
  const computedRounds = Math.ceil(rankingIds.length / teams);
  const prevComputedRoundsRef = useRef<number>(computedRounds);
  const [rounds, setRounds] = useState<number>(savedMobileDraftState?.draft?.rounds ?? 16);

  useEffect(() => {
    const prevComputed = prevComputedRoundsRef.current;
    prevComputedRoundsRef.current = computedRounds;

    setRounds((prev) => {
      const normalized = Number.isFinite(prev) ? Math.max(1, Math.min(40, Math.round(prev))) : computedRounds;

      // If the user never changed rounds (still matches computed), keep tracking computed.
      if (prev === prevComputed) return computedRounds;

      return normalized;
    });
  }, [computedRounds]);

  useEffect(() => {
    setPlannerRoundById((prev) => {
      const next: Record<string, number> = {};
      let changed = false;

      favoriteIds.forEach((playerId) => {
        const currentRound = prev[playerId];
        const defaultRound = getRankedRoundForPlayer(playerId, rankingIdsByList.Rankings, teams, rounds);
        const round = currentRound ? Math.min(rounds, Math.max(1, currentRound)) : defaultRound;
        next[playerId] = round;
        if (prev[playerId] !== round) changed = true;
      });

      if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
  }, [favoriteIds, rankingIdsByList, rounds, teams]);

  useEffect(() => {
    setPlannerOrderedIds((prev) => {
      const next = rankingIdsByList.Rankings.filter((playerId) => favoriteIds.has(playerId));
      if (prev.length === next.length && prev.every((playerId, index) => playerId === next[index])) {
        return prev;
      }

      const remaining = prev.filter((playerId) => favoriteIds.has(playerId));
      const seen = new Set(remaining);
      const additions = next.filter((playerId) => !seen.has(playerId));
      return [...remaining, ...additions];
    });
  }, [favoriteIds, rankingIdsByList]);

  const totalDraftSlots = rounds * teams;

  const [draftSlots, setDraftSlots] = useState<(string | null)[]>(() =>
    Array.from({ length: totalDraftSlots }, (_, index) => savedMobileDraftState?.draft?.draftSlots?.[index] ?? null)
  );
  const [draftSlotTeamIndexes, setDraftSlotTeamIndexes] = useState<Array<number | null>>(() =>
    Array.from({ length: totalDraftSlots }, (_, index) => savedMobileDraftState?.draft?.draftSlotTeamIndexes?.[index] ?? null)
  );

  const assignPlayerToDraftSlot = useCallback((pickIndex: number, playerId: string) => {
    setDraftSlots((prev) => {
      const next = [...prev];
      next[pickIndex] = playerId;
      return next;
    });
  }, []);


  useEffect(() => {
    setDraftSlots((prev) => {
      const next = Array.from({ length: totalDraftSlots }, (_, i) => prev[i] ?? null);
      return next;
    });
    setDraftSlotTeamIndexes((prev) => Array.from({ length: totalDraftSlots }, (_, i) => prev[i] ?? null));
  }, [totalDraftSlots]);

  // ----- DnD sensors (draft board only; rankings board cells are non-sortable) -----
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function onBoardDragEnd(event: DragEndEvent) {
    // Only the editable Rankings list can be reordered.
    if (rankingsListKey !== "Rankings") return;
    // Rankings Board: allow re-ordering the active rankings list via drag & drop.
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const a = String(active.id);
    const b = String(over.id);

    const current = rankingIdsByList[rankingsListKey];
    const fromIndex = current.indexOf(a);
    const toIndex = current.indexOf(b);
    if (fromIndex < 0 || toIndex < 0) return;

    setRankingIdsByList((prev) => {
      const list = prev[rankingsListKey];
      // Defensive: ensure indices still valid for the latest state
      const fi = list.indexOf(a);
      const ti = list.indexOf(b);
      if (fi < 0 || ti < 0 || fi === ti) return prev;
      return { ...prev, [rankingsListKey]: arrayMove(list, fi, ti) };
    });
  }

  function onCompareColumnReorder(fromId: string, toId: string) {
    setCompareColumnOrder((prev) => {
      const fromIndex = prev.indexOf(fromId);
      const toIndex = prev.indexOf(toId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
      return arrayMove(prev, fromIndex, toIndex);
    });
  }

  function onCompareMyRankingsMove(fromId: string, toId: string) {
    setRankingIdsByList((prev) => {
      const current = prev.Rankings;
      const fromIndex = current.indexOf(fromId);
      const toIndex = current.indexOf(toId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
      return { ...prev, Rankings: arrayMove(current, fromIndex, toIndex) };
    });
  }

  function onRemoveCompareColumn(columnId: string) {
    if (columnId === "expert-rankings") {
      clearExpertRankings();
    }
    setCompareColumnOrder((prev) => prev.filter((id) => id !== columnId));
  }

  function clearExpertRankings() {
    setExpertImport(null);
    setExpertPlayers([]);
    setRankingIdsByList((prev) => ({ ...prev, Experts: [] }));
    setTiersByPosByList((prev) => ({ ...prev, Experts: emptyTiersByPos() }));
    setCompareColumnOrder((prev) => prev.filter((id) => id !== "expert-rankings"));
    setRankingsListKey((prev) => (prev === "Experts" ? "Rankings" : prev));
  }

  function onDraftBoardDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const a = String(active.id);
    const b = String(over.id);

    if (!a.startsWith("draftslot:") || !b.startsWith("draftslot:")) return;

    const from = Number(a.split(":")[1]);
    const to = Number(b.split(":")[1]);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;

    setDraftSlots((prev) => {
      const next = [...prev];
      const tmp = next[from] ?? null;
      next[from] = next[to] ?? null;
      next[to] = tmp;
      return next;
    });
  }

  function toggleDrafted(id: string) {
    setDraftedIds((prev) => {
      const next = new Set(prev);
      const wasDrafted = next.has(id);

      if (wasDrafted) next.delete(id);
      else next.add(id);

      setDraftedOrder((prevOrder) => {
        if (wasDrafted) return prevOrder.filter((x) => x !== id);
        if (prevOrder.includes(id)) return prevOrder;
        return [...prevOrder, id];
      });

      setDraftSlots((prevSlots) => {
        const slots = [...prevSlots];

        if (wasDrafted) {
          for (let i = 0; i < slots.length; i++) {
            if (slots[i] === id) slots[i] = null;
          }
          return slots;
        }

        if (slots.includes(id)) return slots;

        const firstEmpty = slots.findIndex((v) => v == null);
        if (firstEmpty >= 0) slots[firstEmpty] = id;

        return slots;
      });

      return next;
    });
  }

  function clearAllDrafted() {
    setDraftedIds(new Set());
    setDraftedOrder([]);
    setDraftSlots(Array.from({ length: totalDraftSlots }, () => null));
    setDraftSlotTeamIndexes(Array.from({ length: totalDraftSlots }, () => null));
    setLastDraftSyncRequest(null);
  }

  const applyDraftSync = useCallback(
    async (payload: DraftSyncPayload) => {
      const applied = applyDraftSyncPayload({
        payload,
        playersById,
        fallbackTeams: teams,
        fallbackRounds: rounds,
        fallbackTeamNames: teamNames,
      });

      setTeams(applied.teams);
      setRounds(applied.rounds);
      if (applied.draftStyle) setDraftStyle(applied.draftStyle);
      setTeamNames(applied.teamNames ?? Array.from({ length: applied.teams }, (_, index) => `Team ${index + 1}`));
      if (applied.createdPlayers.length) {
        setExtraPlayers((prev) => {
          const existingIds = new Set(prev.map((player) => player.id));
          const additions = applied.createdPlayers.filter((player) => !existingIds.has(player.id));
          return additions.length ? [...prev, ...additions] : prev;
        });
      }
      setDraftedIds(new Set(applied.draftedIds));
      setDraftedOrder(applied.draftedOrder);
      setDraftSlots(applied.draftSlots);
      setDraftSlotTeamIndexes(applied.draftSlotTeamIndexes);
      if (applied.rosterStarterConfig) {
        setRosterStarterConfig(applied.rosterStarterConfig);
        setRosterTeamOverrides({});
      }

      return {
        sourceLabel: payload.sourceLabel,
        receivedCount: payload.picks.length,
        importedCount: applied.draftedOrder.length,
        unmatchedCount: applied.unmatchedPicks.length,
        unmatchedExamples: applied.unmatchedPicks.slice(0, 5).map((pick) => pick.playerName || `Pick ${pick.pickNumber}`),
      };
    },
    [playersById, rounds, setExtraPlayers, teamNames, teams]
  );

  const refreshDraftSync = useCallback(async () => {
    if (!lastDraftSyncRequest) return;
    setIsRefreshingDraftSync(true);
    try {
      const payload = await loadDraftSyncRequest(lastDraftSyncRequest);
      await applyDraftSync(payload);
    } finally {
      setIsRefreshingDraftSync(false);
    }
  }, [applyDraftSync, lastDraftSyncRequest]);

  const draftSyncPanel = useMemo(
    () => (
      <DraftSyncPanel
        onApplySync={applyDraftSync}
        onSyncSuccess={(request) => {
          setLastDraftSyncRequest(request);
          setShowDraftSyncModal(false);
        }}
        initialRequest={lastDraftSyncRequest}
      />
    ),
    [applyDraftSync, lastDraftSyncRequest]
  );

  useEffect(() => {
    if (!showDraftSyncModal) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowDraftSyncModal(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDraftSyncModal]);

  useEffect(() => {
    return () => {
      if (importPickerFocusTimeoutRef.current != null) {
        window.clearTimeout(importPickerFocusTimeoutRef.current);
      }
      if (importPickerFallbackTimeoutRef.current != null) {
        window.clearTimeout(importPickerFallbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasHydratedMobileDraftRef.current) {
      hasHydratedMobileDraftRef.current = true;
      return undefined;
    }

    if (saveMobileDraftTimeoutRef.current != null) {
      window.clearTimeout(saveMobileDraftTimeoutRef.current);
    }

    saveMobileDraftTimeoutRef.current = window.setTimeout(() => {
      saveMobileDraftSavedState({
        rankingIdsByList: {
          Rankings: rankingIdsByList.Rankings,
          Experts: rankingIdsByList.Experts,
        },
        tiersByPosByList: {
          Rankings: tiersByPosByList.Rankings,
          Experts: tiersByPosByList.Experts,
        },
        expertImport,
        expertPlayers,
        extraPlayers,
        planner: {
          favoriteIds: Array.from(favoriteIds),
          plannerRoundById,
          plannerOrderedIds,
        },
        draft: {
          teams,
          rounds,
          draftStyle,
          teamNames,
          rosterStarterConfig,
          rosterTeamOverrides,
          draftSlots,
          draftSlotTeamIndexes,
          draftedIds: Array.from(draftedIds),
          draftedOrder,
          lastDraftSyncRequest,
        },
      });
    }, 500);

    return () => {
      if (saveMobileDraftTimeoutRef.current != null) {
        window.clearTimeout(saveMobileDraftTimeoutRef.current);
      }
    };
  }, [
    draftSlotTeamIndexes,
    draftSlots,
    draftStyle,
    draftedIds,
    draftedOrder,
    expertImport,
    expertPlayers,
    extraPlayers,
    favoriteIds,
    lastDraftSyncRequest,
    plannerOrderedIds,
    plannerRoundById,
    rankingIdsByList.Experts,
    rankingIdsByList.Rankings,
    rosterStarterConfig,
    rosterTeamOverrides,
    rounds,
    teamNames,
    teams,
    tiersByPosByList.Experts,
    tiersByPosByList.Rankings,
  ]);

  // ----- tiers normalization (still used for import safety) -----
  function normalizeTierBreaks(pos: Position, breaks: string[], ids: string[]) {
    const posIds = ids.filter((id) => playersById[id]?.position === pos);
    const posSet = new Set(posIds);

    const unique: string[] = [];
    const seen = new Set<string>();
    for (const id of breaks) {
      if (!posSet.has(id)) continue;
      if (seen.has(id)) continue;
      if (posIds.indexOf(id) <= 0) continue;
      seen.add(id);
      unique.push(id);
    }

    unique.sort((a, b) => posIds.indexOf(a) - posIds.indexOf(b));
    return unique;
  }

  // ----- Import / Export (XLSX with 1 sheet: Rankings) -----
  const compareFileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function exportRankingsXlsxClick() {
    const arrayBuffer = exportRankingsXlsx({
      rankingIdsByList,
      playersById,
      tiersByPosByList,
      favoriteRoundById: plannerRoundById,
    });

    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Rankings Export.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function textToBase64(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return window.btoa(binary);
  }

  async function shareRankingsCsvClick() {
    const filename = "Rankings Export.csv";
    const csvText = exportRankingsCsv({
      rankingIds: rankingIdsByList.Rankings ?? [],
      playersById,
      tiersByPos: tiersByPosByList.Rankings,
      favoriteRoundById: plannerRoundById,
    });

    if (!Capacitor.isNativePlatform()) {
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }

    try {
      const result = await Filesystem.writeFile({
        path: filename,
        data: textToBase64(csvText),
        directory: Directory.Cache,
      });

      await Share.share({
        title: filename,
        dialogTitle: "Share Rankings",
        files: [result.uri],
      });
    } catch (error) {
      alert(`Could not open share sheet: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function exportCheatsheetPdfClick() {
    exportCheatsheetPdf({
      rankingIds: rankingIdsByList[rankingsListKey] ?? [],
      playersById,
      tiersByPos: tiersByPosByList[rankingsListKey],
      posColor,
      filename: "Cheatsheet.pdf",
    });
  }

  function shareCheatsheetPdfClick() {
    exportCheatsheetPdf({
      rankingIds: rankingIdsByList[rankingsListKey] ?? [],
      playersById,
      tiersByPos: tiersByPosByList[rankingsListKey],
      posColor,
      filename: "Cheatsheet.pdf",
      options: { share: true },
    }).catch((error) => {
      alert(`Could not open share sheet: ${error instanceof Error ? error.message : String(error)}`);
    });
  }

  function importRankingsXlsxClick() {
    setShowImportConfirmModal(true);
  }

  function clearImportPickerBackdrop() {
    setShowImportPickerBackdrop(false);
    if (importPickerFocusTimeoutRef.current != null) {
      window.clearTimeout(importPickerFocusTimeoutRef.current);
      importPickerFocusTimeoutRef.current = null;
    }
    if (importPickerFallbackTimeoutRef.current != null) {
      window.clearTimeout(importPickerFallbackTimeoutRef.current);
      importPickerFallbackTimeoutRef.current = null;
    }
  }

  function confirmImportRankings() {
    setShowImportConfirmModal(false);
    setShowImportPickerBackdrop(true);

    importPickerFocusTimeoutRef.current = window.setTimeout(() => {
      importPickerFocusTimeoutRef.current = null;
      const onReturnFromPicker = () => clearImportPickerBackdrop();
      window.addEventListener("focus", onReturnFromPicker, { once: true });
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) clearImportPickerBackdrop();
      }, { once: true });
    }, 350);

    importPickerFallbackTimeoutRef.current = window.setTimeout(() => {
      clearImportPickerBackdrop();
    }, 3000);

    window.setTimeout(() => fileInputRef.current?.click(), 0);
  }

  function importExpertRankingsClick() {
    compareFileInputRef.current?.click();
  }

  function mergeImportedPlayers(players: Player[]) {
    const resolvedPlayers = canonicalizePlayers(players);
    const baseById = new Map(basePlayers.map((p) => [p.id, p]));

    setExtraPlayers((prev) => {
      const prevById = new Map(prev.map((p) => [p.id, p]));

      for (const imp of resolvedPlayers) {
        const base = baseById.get(imp.id);
        const { sfValue: _sfValue, ...importedPlayerFields } = imp as any;

        if (base) {
          const existing = prevById.get(imp.id);
          const merged = {
            ...base,
            ...(existing ?? {}),
            ...importedPlayerFields,
            imageUrl:
              (importedPlayerFields as any).imageUrl ||
              (existing as any)?.imageUrl ||
              (base as any).imageUrl ||
              "",
          };
          prevById.set(imp.id, merged);
          continue;
        }

        const existing = prevById.get(imp.id);
        prevById.set(imp.id, existing ? { ...existing, ...importedPlayerFields } : (importedPlayerFields as any));
      }

      return Array.from(prevById.values());
    });
  }

  function importRankingsXlsxFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buf = reader.result as ArrayBuffer;
        const parsed = importRankingsXlsx({ xlsxArrayBuffer: buf });
        const resolvedPlayers = canonicalizePlayers(parsed.players);
        const resolvedRankingIds = canonicalizeRankingIds(parsed.rankingIdsByList.Rankings ?? [], parsed.players);

        // Apply import only to the user-editable Rankings tab.
        if (resolvedRankingIds.length) {
          setRankingIdsByList((prev) => ({ ...prev, Rankings: resolvedRankingIds }));
        }

        setTiersByPosByList((prev) => {
          const next = { ...prev };
          for (const k of ["Rankings"] as const) {
            const v = parsed.tiersByPosByList[k];
            if (v !== undefined) next[k] = v;
          }
          // Normalize against current playersById + ids in each sheet
          for (const k of ["Rankings"] as const) {
            const ids = (k === "Rankings" ? resolvedRankingIds : parsed.rankingIdsByList[k]) ?? rankingIdsByList[k] ?? [];
            next[k] = {
              QB: normalizeTierBreaks("QB", next[k].QB ?? [], ids),
              RB: normalizeTierBreaks("RB", next[k].RB ?? [], ids),
              WR: normalizeTierBreaks("WR", next[k].WR ?? [], ids),
              TE: normalizeTierBreaks("TE", next[k].TE ?? [], ids),
              K: normalizeTierBreaks("K", next[k].K ?? [], ids),
              DST: normalizeTierBreaks("DST", next[k].DST ?? [], ids),
            };
          }
          return next;
        });

        // extra players / overrides
        // NOTE: We store imported player rows into extraPlayers even if the player already exists
        // in the canonical directory. This allows imports to override optional fields like
        // risk/upside/adp without mutating the canonical player source.
        mergeImportedPlayers(resolvedPlayers);

        const importedFavoriteIds = parsed.hasFavoriteRoundsColumn
          ? resolvedRankingIds.filter((id) => parsed.favoriteRoundById[id] != null)
          : [];
        setFavoriteIds(new Set(importedFavoriteIds));
        setPlannerRoundById(() => {
          const next: Record<string, number> = {};
          importedFavoriteIds.forEach((id) => {
            const round = parsed.favoriteRoundById[id];
            if (round != null) next[id] = Math.min(rounds, Math.max(1, round));
          });
          return next;
        });
        setPlannerOrderedIds(importedFavoriteIds);

        alert("Imported rankings + tiers from XLSX.");
      } catch (err: any) {
        alert(`Could not import XLSX: ${err?.message ?? String(err)}`);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function importExpertRankingsFileFromDisk(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buf = reader.result as ArrayBuffer;
        const parsed = importExpertRankingsFile({ fileName: file.name, arrayBuffer: buf });
        const resolvedPlayers = canonicalizePlayers(parsed.players);
        const importedIds = canonicalizeRankingIds(parsed.rankingIds ?? [], parsed.players);
        const resolvedTiersByPos: TiersByPos = {
          QB: normalizeImportedTierBreaks("QB", canonicalizeRankingIds(parsed.tiersByPos?.QB ?? [], parsed.players), importedIds, resolvedPlayers),
          RB: normalizeImportedTierBreaks("RB", canonicalizeRankingIds(parsed.tiersByPos?.RB ?? [], parsed.players), importedIds, resolvedPlayers),
          WR: normalizeImportedTierBreaks("WR", canonicalizeRankingIds(parsed.tiersByPos?.WR ?? [], parsed.players), importedIds, resolvedPlayers),
          TE: normalizeImportedTierBreaks("TE", canonicalizeRankingIds(parsed.tiersByPos?.TE ?? [], parsed.players), importedIds, resolvedPlayers),
          K: normalizeImportedTierBreaks("K", canonicalizeRankingIds(parsed.tiersByPos?.K ?? [], parsed.players), importedIds, resolvedPlayers),
          DST: normalizeImportedTierBreaks("DST", canonicalizeRankingIds(parsed.tiersByPos?.DST ?? [], parsed.players), importedIds, resolvedPlayers),
        };
        if (!importedIds.length) {
          alert("No expert rankings found in that file.");
          return;
        }

        const fileLabel = file.name.replace(/\.[^.]+$/, "").trim() || "Imported Rankings";
        const shouldSeedMyRankings = rankingIdsByList.Rankings.length === 0;

        setExpertPlayers(resolvedPlayers);
        setRankingIdsByList((prev) => ({
          ...prev,
          Rankings: prev.Rankings.length ? prev.Rankings : importedIds,
          Experts: importedIds,
        }));
        setTiersByPosByList((prev) => ({
          ...prev,
          Rankings: shouldSeedMyRankings ? resolvedTiersByPos : prev.Rankings,
          Experts: resolvedTiersByPos,
        }));
        setExpertImport({
          id: "expert-rankings",
          title: fileLabel,
          rankingIds: importedIds,
        });
        setRankingsListKey((current) => (current === "Experts" ? "Experts" : current));
        setCompareColumnOrder((prev) => {
          const filtered = prev.filter((id) => id !== "expert-rankings");
          return ["my-rankings", "expert-rankings", ...filtered.filter((id) => id !== "my-rankings")];
        });
      } catch (err: any) {
        alert(`Could not import expert rankings: ${err?.message ?? String(err)}`);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const compareColumns = useMemo<CompareRankingsColumn[]>(() => {
    const baseColumns: CompareRankingsColumn[] = [
      {
        id: "my-rankings",
        title: "My Rankings",
        subtitle: "Editable list",
        rankingIds: rankingIdsByList.Rankings,
        accent: "#22c55e",
        editable: true,
      },
    ];

    if (expertImport) {
      baseColumns.push({
        id: "expert-rankings",
        title: "Expert Rankings",
        subtitle: expertImport.title,
        rankingIds: expertImport.rankingIds,
        accent: "#38bdf8",
        removable: true,
      });
    }

    const byId = new Map(baseColumns.map((column) => [column.id, column]));
    const ordered = compareColumnOrder.map((id) => byId.get(id)).filter((column): column is CompareRankingsColumn => Boolean(column));
    const unordered = baseColumns.filter((column) => !compareColumnOrder.includes(column.id));
    return [...ordered, ...unordered];
  }, [
    rankingIdsByList,
    expertImport,
    compareColumnOrder,
  ]);

  return (
    <div className={isMobile ? "appViewport appViewport--mobileDraft" : "appViewport"}>
      {showImportPickerBackdrop && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4090,
            background: "rgba(6,8,16,0.72)",
            backdropFilter: "blur(4px)",
          }}
        />
      )}

      {showImportConfirmModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4100,
            background: "rgba(6,8,16,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? 14 : 18,
          }}
          onPointerDown={() => setShowImportConfirmModal(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 360,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(12,18,34,0.98)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
              padding: isMobile ? 14 : 16,
              display: "grid",
              gap: 12,
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Import rankings?</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                This will replace your custom rankings, tiers, and favorites. It will also clear your draft planner.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setShowImportConfirmModal(false)}
                style={{
                  padding: isMobile ? "7px 10px" : "8px 13px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--text-0)",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmImportRankings}
                style={{
                  padding: isMobile ? "7px 10px" : "8px 13px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.92)",
                  color: "#0f172a",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 900,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {showDraftSyncModal && (
        <div
          onClick={() => setShowDraftSyncModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4000,
            background: "rgba(0,0,0,0.58)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(1180px, 100%)",
              maxHeight: "min(94vh, 1100px)",
              overflow: "auto",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(10,14,28,0.98)",
              boxShadow: "0 28px 70px rgba(0,0,0,0.5)",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setShowDraftSyncModal(false)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "var(--text-0)",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
            {draftSyncPanel}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 16,
        }}
      >
        {!isMobile && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <div className="appHeaderWrap">
              <TopBanner
                onExportRankings={exportRankingsXlsxClick}
                onExportCheatsheetPdf={exportCheatsheetPdfClick}
                onImportRankings={importRankingsXlsxClick}
                onOpenDraftSync={() => setShowDraftSyncModal(true)}
                uiScale={desktopUiScale}
              />
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: "none" }}
          onChange={(e) => {
            clearImportPickerBackdrop();
            const file = e.target.files?.[0];
            if (!file) return;
            importRankingsXlsxFile(file);
            e.currentTarget.value = "";
          }}
        />

        <input
          ref={compareFileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            importExpertRankingsFileFromDisk(file);
            e.currentTarget.value = "";
          }}
        />

        {/* Main content */}
        {isMobile ? (
          <MobileDraftCompanionView
            favoriteIds={favoriteIds}
            plannerRoundById={plannerRoundById}
            setPlannerRoundById={setPlannerRoundById}
            plannerOrderedIds={plannerOrderedIds}
            setPlannerOrderedIds={setPlannerOrderedIds}
            boardTab={boardTab}
            setBoardTab={setBoardTab}
            rounds={rounds}
            setRounds={setRounds}
            teams={teams}
            setTeams={setTeams}
            onAddPlayer={addPlayerToRankings}
            draftStyle={draftStyle}
            setDraftStyle={setDraftStyle}
            rankingsListKey={rankingsListKey}
            setRankingsListKey={setRankingsListKey}
            rankingIds={boardRankingIds}
            rankingsRankingIds={rankingIdsByList["Rankings"]}
            playersById={playersById}
            tiersByPos={boardTiersByPos}
            onUpdateTiersByPos={onUpdateTiersByPos}
            draftedIds={draftedIds}
            onToggleDrafted={toggleDrafted}
            clearAllDrafted={clearAllDrafted}
            teamNames={teamNames}
            setTeamNames={setTeamNames}
            draftSlots={draftSlots}
            draftSlotTeamIndexes={draftSlotTeamIndexes}
            rosterStarterConfig={rosterStarterConfig}
            setRosterStarterConfig={setRosterStarterConfig}
            rosterTeamOverrides={rosterTeamOverrides}
            setRosterTeamOverrides={setRosterTeamOverrides}
            onAssignPlayerToDraftSlot={assignPlayerToDraftSlot}
            posColor={posColor}
            sensors={sensors}
            onBoardDragEnd={onBoardDragEnd}
            onDraftBoardDragEnd={onDraftBoardDragEnd}
            onMoveRankings={moveRankings}
            onImportRankings={importRankingsXlsxClick}
            onShareRankings={shareRankingsCsvClick}
            onExportCheatsheetPdf={shareCheatsheetPdfClick}
            onImportExpertRankings={importExpertRankingsClick}
            onRemoveExpertRankings={clearExpertRankings}
            onSetAsRankings={setActiveSourceAsRankings}
            hideKickers={hideKickers}
            setHideKickers={setHideKickers}
            hideDefenses={hideDefenses}
            setHideDefenses={setHideDefenses}
            onToggleFavorite={toggleFavorite}
            onRemoveAddedPlayer={removeAddedPlayer}
            expertRankingIds={rankingIdsByList.Experts}
            onOpenDraftSync={() => setShowDraftSyncModal(true)}
            onRefreshDraftSync={lastDraftSyncRequest ? refreshDraftSync : undefined}
            isRefreshingDraftSync={isRefreshingDraftSync}
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "clamp(370px, 22vw, 540px) minmax(0, 1fr)",
              gridTemplateRows: "1fr",
              columnGap: 12,
              rowGap: 12,
              flex: 1,
              minHeight: 0,
              width: "100%",
              justifyContent: "stretch",
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                border: "1px solid var(--border-0)",
                background: "var(--panel-bg)",
                borderRadius: 12,
                padding: 8,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <RankingsList
                uiScale={desktopUiScale}
                rankingsListKey={rankingsListKey}
                setRankingsListKey={setRankingsListKey}
                rankingIds={rankingIds}
                rankingsRankingIds={rankingIdsByList["Rankings"]}
                playersById={playersById}
                tiersByPos={tiersByPos}
                draftedIds={draftedIds}
                onToggleDrafted={toggleDrafted}
                hideKickers={hideKickers}
                setHideKickers={setHideKickers}
                hideDefenses={hideDefenses}
                setHideDefenses={setHideDefenses}
                favoriteIds={favoriteIds}
                onToggleFavorite={toggleFavorite}
                onRemoveAddedPlayer={removeAddedPlayer}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                getColor={posColor}
                onMove={moveRankings}
                onSetAsRankings={setActiveSourceAsRankings}
                onImportExpertRankings={importExpertRankingsClick}
                onRemoveExpertRankings={clearExpertRankings}
                expertRankingIds={rankingIdsByList.Experts}
              />
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="boardScroll">
                <Board
                  uiScale={desktopUiScale}
                  allowRankingsReorder={rankingsListKey === "Rankings"}
                  favoriteIds={favoriteIds}
                  plannerRoundById={plannerRoundById}
                  setPlannerRoundById={setPlannerRoundById}
                  plannerOrderedIds={plannerOrderedIds}
                  setPlannerOrderedIds={setPlannerOrderedIds}
                  boardTab={boardTab}
                  setBoardTab={setBoardTab}
                  rounds={rounds}
                  setRounds={setRounds}
                  teams={teams}
                  setTeams={setTeams}
                  onAddPlayer={addPlayerToRankings}
                  draftStyle={draftStyle}
                  setDraftStyle={setDraftStyle}
                  rankingIds={boardRankingIds}
                  playersById={playersById}
                  tiersByPos={boardTiersByPos}
                  onUpdateTiersByPos={onUpdateTiersByPos}
                  draftedIds={draftedIds}
                  onToggleDrafted={toggleDrafted}
                  onToggleFavorite={toggleFavorite}
                  clearAllDrafted={clearAllDrafted}
                  teamNames={teamNames}
                  setTeamNames={setTeamNames}
                  draftSlots={draftSlots}
                  draftSlotTeamIndexes={draftSlotTeamIndexes}
                  rosterStarterConfig={rosterStarterConfig}
                  setRosterStarterConfig={setRosterStarterConfig}
                  rosterTeamOverrides={rosterTeamOverrides}
                  setRosterTeamOverrides={setRosterTeamOverrides}
                  onAssignPlayerToDraftSlot={assignPlayerToDraftSlot}
                  posColor={posColor}
                  sensors={sensors}
                  onBoardDragEnd={onBoardDragEnd}
                  onDraftBoardDragEnd={onDraftBoardDragEnd}
                  compareColumns={compareColumns}
                  onImportCompareRankings={importExpertRankingsClick}
                  onReorderCompareColumns={onCompareColumnReorder}
                  onMoveMyCompareRankings={onCompareMyRankingsMove}
                  onRemoveCompareColumn={onRemoveCompareColumn}
                  onOpenDraftSync={() => setShowDraftSyncModal(true)}
                  onRefreshDraftSync={lastDraftSyncRequest ? refreshDraftSync : undefined}
                  isRefreshingDraftSync={isRefreshingDraftSync}
                  hideKickers={hideKickers}
                  hideDefenses={hideDefenses}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
